import { Form } from "antd";
import { dcaApis, deliveryApis, iamApis, orderApis, sellerAndPlatformApis, ticketApis } from "apis";
import { AxiosError } from "axios";
import {
  EXPORT_DATE_RANGE_LIMIT,
  GET_CUSTOMER_LIST_PARAMS,
  MIN_LENGTH_TO_SEARCH_BY_PHONE,
  flagConstants,
} from "constants/index";
import { StoreContext, initialMarketOrderDetail, initialOrderDetails, useSellerState } from "contexts";
import lodashGet from "lodash/get";
import {
  BillingTaxType,
  CommentFormValues,
  CommentsAndAssignee,
  CustomerCreatePayload,
  CustomerGetListItem,
  CustomerGetListParams,
  DcaAccountGetListPayload,
  DcaAccountRoleId,
  ErrorInfoDomain,
  GetTotalFilteredOrderPayload,
  IBffTerminal,
  IConsultant,
  IDcaAccount,
  IOrderCustomer,
  IPlatform,
  ISeller,
  MarketOrder,
  Order,
  OrderBillingInfoUpdatePayload,
  OrderConsultantUpdatePayload,
  OrderCustomerUpdatePayload,
  OrderDetails,
  OrderFilter,
  OrderGetListPayload,
  PostOrderCommentPayload,
  SellerOrder,
  Shipment,
  SellerOrderFilter,
  SellerOrderGetListPayload,
  StaffOfTeam,
  StaffUserDetail,
  TeamOption,
  UserGetListPayload,
} from "models";
import { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { commonUtils, deliveryRequestUtils, numberUtils, orderUtils, requestUtils, sellerUtils } from "utils";
import { t } from "utils/i18n";
import { tryCatch } from "utils/independent";

const { FLAG_KEYS, EXTENDED_FLAG_KEYS } = flagConstants;
const CROSS_PLATFORM_SEARCH_REASON = "ORDER_PLATFORM_NOT_MATCHED_BUT_CAN_SEARCH_CROSS_PLATFORM";

const { mapShipmentOptionsToShipmentStatus, getOrderStatusFilter } = orderUtils;
const { validateDateRangeList } = commonUtils;
const { getNumberDisplayFormat } = numberUtils;

const useMarketOrderListData = (defaultFilter: OrderFilter, initOrders: boolean) => {
  /* ---------------------------------- state --------------------------------- */
  const { featureFlagsData } = useContext(StoreContext);
  const isOMNI1058Enabled = featureFlagsData[FLAG_KEYS.UNIFY_ORDER_STATUS_LABEL]?.enabled;

  const ORDER_STATUS_FILTER = getOrderStatusFilter(isOMNI1058Enabled);

  const [orders, setOrders] = useState<Order[]>([]);
  const [totalFilteredOrders, setTotalFilteredOrders] = useState<number | undefined>();
  const [filters, setFilters] = useState(defaultFilter);
  const [fetching, setFetching] = useState(false);
  const [fetchingCount, setFetchingCount] = useState(false);
  const [nextOffset, setNextOffset] = useState<number>(0);
  const [canLoadMore, setCanLoadMore] = useState(false);
  const [shouldGetOrders, setShouldGetOrders] = useState(initOrders);

  /* -------------------------------- functions ------------------------------- */
  const transformFilterToParams = (filter: OrderFilter) => {
    return {
      ...(filter.orderStatus && ORDER_STATUS_FILTER[filter.orderStatus].params),
      shipmentStatus: mapShipmentOptionsToShipmentStatus(filter.shipmentStatus),
      phone: filter.phone?.trim(),
      createdAtGte: filter.orderCreatedDate?.[0]?.format("YYYY-MM-DDTHH:mm:ssZ"),
      createdAtLte: filter.orderCreatedDate?.[1]?.format("YYYY-MM-DDTHH:mm:ssZ"),
      deliveredAtGte: filter.deliveredDate?.[0]?.format("YYYY-MM-DDTHH:mm:ssZ"),
      deliveredAtLte: filter.deliveredDate?.[1]?.format("YYYY-MM-DDTHH:mm:ssZ"),
      paidAtGte: filter.paymentDate?.[0]?.format("YYYY-MM-DDTHH:mm:ssZ"),
      paidAtLte: filter.paymentDate?.[1]?.format("YYYY-MM-DDTHH:mm:ssZ"),
      sellerId: filter.sellerId,
      terminalId: filter.terminalId,
      platformId: filter.platformId,
      consultantId: filter.consultantId,
      limit: filter.limit,
      offset: filter.offset,
      sort: filter.sort,
      isReadyToFulfill: filter.isReadyToFulfill,
      exportSiteIds: filter.exportSiteIds,
      skus: filter.skus,
    };
  };

  const transformOrdersListPayload2TotalFilteredOrderPayload = (
    ordersListPayload: OrderGetListPayload
  ): GetTotalFilteredOrderPayload => {
    const { phone, offset, limit, sort, ...rest } = ordersListPayload;
    return {
      ...rest,
      customer: ordersListPayload.phone,
    };
  };

  const getOrders = async (params: OrderFilter) => {
    try {
      setFetching(true);
      const { orders, nextOffset } = await orderApis.getMarketOrdersList(transformFilterToParams(params));
      if (orders) {
        setCanLoadMore(orders.length === (filters.limit || 0));
        setOrders(orders);
        setNextOffset(nextOffset);
      }
    } finally {
      setFetching(false);
    }
  };

  const getTotalFilteredOrders = async (params: OrderFilter) => {
    try {
      setFetchingCount(true);
      const isDatesValid =
        params.orderCreatedDate &&
        validateDateRangeList(
          EXPORT_DATE_RANGE_LIMIT,
          params.orderCreatedDate,
          params.deliveredDate,
          params.paymentDate
        );
      if (isDatesValid) {
        const { orderCount } = await orderApis.getTotalFilteredOrders(
          transformOrdersListPayload2TotalFilteredOrderPayload(transformFilterToParams(params))
        );
        setTotalFilteredOrders(orderCount);
      } else {
        setTotalFilteredOrders(undefined);
      }
    } catch {
      setTotalFilteredOrders(undefined);
    } finally {
      setFetchingCount(false);
    }
  };

  const handleLoadMore = async () => {
    try {
      setFetching(true);
      const params = {
        ...transformFilterToParams(filters),
        offset: nextOffset,
      };
      const { orders, nextOffset: resNextOffset } = await orderApis.getMarketOrdersList(params);
      if (orders) {
        setCanLoadMore(orders.length === (filters.limit || 0));
        setOrders((prevState) => [...prevState, ...orders]);
        setNextOffset(resNextOffset);
      }
    } finally {
      setFetching(false);
    }
  };

  /* ---------------------------------- hooks --------------------------------- */

  useEffect(() => {
    if (shouldGetOrders && filters.platformId) {
      getOrders(filters);
      getTotalFilteredOrders(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return {
    orders,
    totalFilteredOrders,
    filters,
    setFilters,
    fetching,
    fetchingCount,
    handleLoadMore,
    canLoadMore,
    shouldGetOrders,
    setShouldGetOrders,
  };
};

const useSellerOrderListData = (defaultFilter: SellerOrderFilter, currentSellerId: ISeller["id"]) => {
  const { featureFlagsData } = useContext(StoreContext);
  const enabledGetUserTerminals = featureFlagsData[EXTENDED_FLAG_KEYS.GET_USER_TERMINALS_SELLER.key]?.enabled;

  const [orders, setOrders] = useState<SellerOrder[]>([]);
  const [totalFilteredOrders, setTotalFilteredOrders] = useState<number | undefined>();
  const [filters, setFilters] = useState<SellerOrderFilter>(defaultFilter);
  const [fetching, setFetching] = useState(false);
  const [fetchingCount, setFetchingCount] = useState(false);
  const [sellingPlatforms, setSellingPlatforms] = useState<IPlatform[]>([]);
  const [isFetchingSellingPlatforms, setIsFetchingSellingPlatforms] = useState(false);
  const [isFetchingTerminals, setIsFetchingTerminals] = useState(false);
  const [terminals, setTerminals] = useState<IBffTerminal[]>([]);
  const [nextOffset, setNextOffset] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState(false);

  /* -------------------------------- functions ------------------------------- */
  const transformFilterToParams = (filter: SellerOrderFilter) => {
    return {
      orderId: filter.orderId?.trim() || undefined,
      phone: filter.phone?.trim(),
      consultantId: filter.consultantId,
      shipmentStatus: mapShipmentOptionsToShipmentStatus(filter.shipmentStatus),
      platformId: filter.platformId,
      terminalId: filter.terminalId,
      skus: filter.skus,
      createdAtGte: filter.orderCreatedDate?.[0]?.format("YYYY-MM-DDTHH:mm:ssZ"),
      createdAtLte: filter.orderCreatedDate?.[1]?.format("YYYY-MM-DDTHH:mm:ssZ"),
      paidAtGte: filter.paymentDate?.[0]?.format("YYYY-MM-DDTHH:mm:ssZ"),
      paidAtLte: filter.paymentDate?.[1]?.format("YYYY-MM-DDTHH:mm:ssZ"),
      deliveredAtGte: filter.deliveredDate?.[0]?.format("YYYY-MM-DDTHH:mm:ssZ"),
      deliveredAtLte: filter.deliveredDate?.[1]?.format("YYYY-MM-DDTHH:mm:ssZ"),
      limit: filter.limit,
      offset: filter.offset,
      sort: filter.sort,
      exportSiteIds: filter.exportSiteIds,
    };
  };

  const transformSellerOrdersListPayload2SellerTotalFilteredOrderPayload = (
    sellerOrdersListPayload: SellerOrderGetListPayload
  ): SellerOrderGetListPayload => {
    const { offset, limit, sort, ...rest } = sellerOrdersListPayload;
    return {
      ...rest,
    };
  };

  const getOrders = async (params: SellerOrderFilter) => {
    try {
      setFetching(true);
      const { sellerOrders: orders, nextOffset } = await orderApis.getSellerOrdersList(transformFilterToParams(params));
      if (orders) {
        setCanLoadMore(orders.length === (filters.limit || 0));
        setOrders(orders);
        setNextOffset(nextOffset);
      }
    } finally {
      setFetching(false);
    }
  };

  const getTotalFilteredOrders = async (params: SellerOrderFilter) => {
    try {
      setFetchingCount(true);
      const isDatesValid =
        params.orderCreatedDate &&
        validateDateRangeList(
          EXPORT_DATE_RANGE_LIMIT,
          params.orderCreatedDate,
          params.deliveredDate,
          params.paymentDate
        );
      if (isDatesValid) {
        const { shipmentCount } = await orderApis.getSellerTotalFilteredOrders(
          transformSellerOrdersListPayload2SellerTotalFilteredOrderPayload(transformFilterToParams(params))
        );
        setTotalFilteredOrders(shipmentCount);
      } else {
        setTotalFilteredOrders(undefined);
      }
    } catch {
      setTotalFilteredOrders(undefined);
    } finally {
      setFetchingCount(false);
    }
  };

  const getSellingPlatforms = useCallback(async () => {
    if (currentSellerId) {
      setIsFetchingSellingPlatforms(true);
      const result = await sellerAndPlatformApis.getSellingPlatforms(currentSellerId);
      setIsFetchingSellingPlatforms(false);
      setSellingPlatforms(result);
    }
  }, [currentSellerId]);

  const getTerminalsBySeller = useCallback(async () => {
    if (currentSellerId) {
      setIsFetchingTerminals(true);
      const response = enabledGetUserTerminals
        ? await sellerAndPlatformApis.getUserTerminals({ sellerId: currentSellerId })
        : await sellerAndPlatformApis.getTerminalsBySeller(currentSellerId);
      setIsFetchingTerminals(false);
      if (!!response.length) {
        setTerminals(response);
      }
    }
  }, [currentSellerId, enabledGetUserTerminals]);

  const handleLoadMore = async () => {
    try {
      setFetching(true);
      const params = {
        ...transformFilterToParams(filters),
        offset: nextOffset,
      };
      const { sellerOrders: orders, nextOffset: resNextOffset } = await orderApis.getSellerOrdersList(params);
      if (orders) {
        setCanLoadMore(orders.length === (filters.limit || 0));
        setOrders((prevState) => [...prevState, ...orders]);
        setNextOffset(resNextOffset);
      }
    } finally {
      setFetching(false);
    }
  };

  /* ---------------------------------- hooks --------------------------------- */
  useEffect(() => {
    getOrders(filters);
    getTotalFilteredOrders(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    getSellingPlatforms();
  }, [getSellingPlatforms]);

  useEffect(() => {
    getTerminalsBySeller();
  }, [getTerminalsBySeller]);

  return {
    orders,
    totalFilteredOrders,
    filters,
    setFilters,
    fetching,
    fetchingCount,
    sellingPlatforms,
    isFetchingSellingPlatforms,
    isFetchingTerminals,
    terminals,
    canLoadMore,
    handleLoadMore,
  };
};

const useMarketOrderDetail = (orderId: string) => {
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<MarketOrder>(initialMarketOrderDetail);

  const fetchData = useCallback(async () => {
    if (orderId) {
      setLoading(true);
      try {
        const order: MarketOrder = await orderApis.getMarketOrderDetail(orderId);

        setOrder({ ...orderUtils.switchItemsToRouterItems(order) });
      } finally {
        setLoading(false);
      }
    }
  }, [orderId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  return {
    loading,
    order,
    fetchData,
  };
};

const useOrderDetailsForPlatform = (orderId: string) => {
  const { featureFlagsData } = useContext(StoreContext);
  const { currentSeller } = useSellerState();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<OrderDetails>(initialOrderDetails);

  const isOmni1058Enabled = featureFlagsData[FLAG_KEYS.UNIFY_ORDER_STATUS_LABEL]?.enabled;
  const isAllowSearchCrossPlatform = featureFlagsData[EXTENDED_FLAG_KEYS.ALLOW_SEARCH_CROSS_PLATFORM.key]?.enabled;

  const isCrossPlatformSearchError = (error: AxiosError) => {
    const details = error?.response?.data?.details;
    if (!Array.isArray(details)) {
      return false;
    }

    return details.some(
      (detail) =>
        detail?.value?.domain === ErrorInfoDomain.StaffOMAdminBFF &&
        detail?.value?.reason === CROSS_PLATFORM_SEARCH_REASON
    );
  };

  const fetchData = useCallback(async () => {
    if (orderId) {
      setLoading(true);
      try {
        const { order } = await orderApis.getOrderDetailsForPlatform(
          orderId,
          getNumberDisplayFormat(),
          isAllowSearchCrossPlatform ? { skipNotification: true } : undefined
        );

        const transformedOrder = {
          ...orderUtils.transformRawOrderDetails(order, isOmni1058Enabled),
        };
        setOrder(transformedOrder);

        // just to adapt the sync flow of refreshWithGuarantee
        return transformedOrder;
      } catch (error) {
        if (!isAllowSearchCrossPlatform) {
          throw error;
        }

        if (isCrossPlatformSearchError(error as AxiosError)) {
          history.replace(`/orders/seller/${currentSeller?.id}/${orderId}`);
          return;
        }

        requestUtils.handleResponseError(error as AxiosError);
      } finally {
        setLoading(false);
      }
    }
  }, [orderId, isOmni1058Enabled, currentSeller, isAllowSearchCrossPlatform, history]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    order,
    fetchData,
  };
};

const useOrderDetailsForSeller = (orderId: string) => {
  const { featureFlagsData } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [sellerOrder, setSellerOrder] = useState<OrderDetails>(initialOrderDetails);

  const isOmni1058Enabled = featureFlagsData[FLAG_KEYS.UNIFY_ORDER_STATUS_LABEL]?.enabled;

  const fetchData = useCallback(async () => {
    if (orderId) {
      setLoading(true);
      try {
        const { sellerOrder } = await orderApis.getOrderDetailsForSeller(orderId, getNumberDisplayFormat());

        const transformedOrder = {
          ...orderUtils.transformRawOrderDetails(sellerOrder, isOmni1058Enabled),
        };
        setSellerOrder(transformedOrder);

        // just to adapt the sync flow of refreshWithGuarantee
        return transformedOrder;
      } finally {
        setLoading(false);
      }
    }
  }, [orderId, isOmni1058Enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    sellerOrder,
    fetchData,
  };
};

const useSellerOrderDetail = (orderId: MarketOrder["id"]) => {
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<MarketOrder>(initialMarketOrderDetail);

  const fetchData = useCallback(async () => {
    if (orderId) {
      setLoading(true);
      try {
        const order = await orderApis.getSellerOrderDetail(orderId);
        setOrder(orderUtils.switchItemsToRouterItems(order));
      } finally {
        setLoading(false);
      }
    }
  }, [orderId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    order,
    fetchData,
  };
};

const useAuthorizedPlatforms = () => {
  const [authorizedPlatforms, setAuthorizedPlatforms] = useState<IPlatform[]>([]);
  const [isFetching, setIsFetching] = useState(true);
  const { currentUser } = useContext(StoreContext);
  const userPlatformId = Number.parseInt(lodashGet(currentUser, ["meta_data", "platformId"]));

  const fetchPlatforms = useCallback(async () => {
    if (Number.isInteger(userPlatformId)) {
      const canAccessAllPlatforms = sellerUtils.hasAllPlatforms(currentUser);
      const platforms = canAccessAllPlatforms
        ? await sellerAndPlatformApis.getAllPlatforms()
        : await sellerAndPlatformApis.getPlatform(userPlatformId).then((platform) => (platform ? [platform] : []));
      setAuthorizedPlatforms(platforms);
    }
    setIsFetching(false);
  }, [userPlatformId, currentUser]);

  useEffect(() => {
    fetchPlatforms();
  }, [fetchPlatforms]);

  return {
    authorizedPlatforms: authorizedPlatforms?.sort((a, b) => a.name.localeCompare(b.name)),
    isFetching,
    userPlatformId,
  };
};

interface RenderData {
  isFetchingComments: boolean;
  comments: CommentsAndAssignee["comments"];
  assignee: CommentsAndAssignee["assignee"];
  teamOptions: CommentsAndAssignee["teamOptions"];
  assigneeOptions: StaffOfTeam[];
}

const useOrderComment = (orderId: string) => {
  const [renderData, setRenderData] = useState<RenderData>({
    assignee: {},
    comments: [],
    teamOptions: [],
    isFetchingComments: false,
    assigneeOptions: [],
  });
  const [selectedTeamId, setSelectedTeamId] = useState<TeamOption["teamId"]>();
  const [ticketId, setTicketId] = useState<number>();
  const [isBlocking, setIsBlocking] = useState(false);
  const [isSubmittable, setIsSubmittable] = useState(false);
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [form] = Form.useForm<CommentFormValues>();
  const [hasAssigneeError, setHasAssigneeError] = useState(false);

  const fetchRenderData = useCallback(async () => {
    try {
      setRenderData((prev) => ({ ...prev, isFetchingComments: true }));
      const getCAARes = await orderApis.getCommentsAndAssignee(orderId);
      const { assignee, comments, teamOptions } = getCAARes;
      if (getCAARes) {
        setRenderData((prev) => ({
          ...prev,
          assignee,
          comments,
          teamOptions,
        }));

        if (getCAARes.ticketId) {
          setTicketId(getCAARes.ticketId);
        }
      }

      if (!!teamOptions.length) {
        const selectedTeam = teamOptions.find((teamOption) => teamOption.selected);
        if (selectedTeam && selectedTeam.teamId) {
          form.setFieldsValue({ team: selectedTeam.teamId });
          setSelectedTeamId(selectedTeam.teamId);

          const staffs = await ticketApis.getStaffsOfTeamOrEmpty(selectedTeam.teamId);
          if (staffs && !!staffs.length) {
            setRenderData((prev) => ({ ...prev, assigneeOptions: staffs }));
            if (assignee && assignee?.staffId) {
              if (isStaffInTeam(staffs, assignee.staffId)) {
                form.setFieldsValue({ assignee: assignee.staffId });
              } else {
                form.setFieldsValue({ assignee: undefined });
              }
            }
          }
        }
      }
    } finally {
      setRenderData((prev) => ({ ...prev, isFetchingComments: false }));
    }
  }, [form, orderId]);

  const isStaffInTeam = (staffOptions: StaffOfTeam[], staffId: number): boolean => {
    return staffOptions.some((option) => option.id === staffId);
  };

  const fetchStaffOptions = useCallback(
    async (teamId) => {
      try {
        setRenderData((prev) => ({ ...prev, isFetchingComments: true }));
        const staffs = await ticketApis.getStaffsOfTeamOrEmpty(teamId);
        if (staffs && !!staffs.length) {
          setRenderData((prev) => ({ ...prev, assigneeOptions: staffs }));
          const currentStaffId = renderData?.assignee?.staffId;
          if (currentStaffId && !isStaffInTeam(staffs, currentStaffId)) {
            form.setFieldsValue({ assignee: undefined });
            setHasAssigneeError(true);
          } else {
            setHasAssigneeError(false);
            form.setFieldsValue({ assignee: currentStaffId });
          }
        }
      } finally {
        setRenderData((prev) => ({ ...prev, isFetchingComments: false }));
      }
    },
    [form, renderData.assignee?.staffId]
  );

  const postComment = async (values: CommentFormValues) => {
    try {
      setIsPostingComment(true);
      const payload: PostOrderCommentPayload = {
        assigneeStaffId: values?.assignee,
        content: values?.content,
        teamId: values?.team,
        ticketId: ticketId,
      };
      await orderApis.postOrderComment(orderId, payload);
      form.resetFields();
      await fetchRenderData();
    } finally {
      setIsPostingComment(false);
    }
  };

  useEffect(() => {
    fetchRenderData();
  }, [fetchRenderData]);

  return {
    form,
    renderData,
    isBlocking,
    isPostingComment,
    isSubmittable,
    selectedTeamId,
    hasAssigneeError,
    postComment,
    fetchStaffOptions,
    setIsBlocking,
    setIsSubmittable,
    isStaffInTeam,
    setHasAssigneeError,
  };
};

const useOrderConsultant = () => {
  const [consultants, setConsultants] = useState<StaffUserDetail[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [searchHelpText, setSearchHelpText] = useState("");
  const { currentUser } = useContext(StoreContext);
  const { tenant_id } = currentUser;

  const searchByEmailOrPhone = async (searchText: string) => {
    searchText = searchText.trim();

    if (orderUtils.isInEmailFormat(searchText)) {
      setSearchHelpText("");
      const userDetailList = await getUserDetailList({ email: searchText });
      setConsultants(userDetailList);
    } else if (orderUtils.isInPhoneFormat(searchText)) {
      setSearchHelpText("");
      const userDetailList = await getUserDetailList({ phone_number: searchText });
      setConsultants(userDetailList);
    } else {
      setSearchHelpText(t("InputStaffExactEmailOrPhoneToSearch"));
    }
  };

  const getUserDetailList = async (params: UserGetListPayload): Promise<StaffUserDetail[]> => {
    try {
      setIsFetching(true);
      const userList = await iamApis.getUserListOrEmpty({
        revoked: false,
        active: true,
        fields: "id,name,email,phone_number",
        tenant_id,
        ...params,
      });
      if (!!userList.length) {
        const reqList = userList.reduce((previousValue: Promise<StaffUserDetail>[], currentValue: StaffUserDetail) => {
          if (currentValue?.id) {
            const res = iamApis.getUserDetail(currentValue.id);
            previousValue.push(res);
          }
          return previousValue;
        }, []);
        let userDetailList = await Promise.all(reqList);
        return userDetailList;
      }
      return [];
    } catch (err) {
      return [];
    } finally {
      setIsFetching(false);
    }
  };

  const updateConsultant = async (orderId: MarketOrder["id"], value: StaffUserDetail) => {
    try {
      setIsFetching(true);
      const payload: OrderConsultantUpdatePayload = {
        consultant: {
          id: value?.id,
          email: value?.email,
          name: value?.name,
          phone: value?.phone_number,
          customId: value.user_metadata?.metadata?.externalUserId,
        },
      };
      await orderApis.updateOrderConsultant(orderId, payload);
      return { success: true, newConsultant: payload.consultant as IConsultant };
    } catch (err) {
      return { success: false, newConsultant: undefined };
    } finally {
      setIsFetching(false);
    }
  };

  return {
    isFetching,
    consultants,
    searchHelpText,
    updateConsultant,
    searchByEmailOrPhone,
  };
};

const useOrderCustomer = (platformId: MarketOrder["platformId"]) => {
  const [customers, setCustomers] = useState<CustomerGetListItem[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const defaultParams: CustomerGetListParams = {
    platformId: platformId,
    sortField: GET_CUSTOMER_LIST_PARAMS.SORT_FIELD.UPDATED_AT,
    sortOrder: GET_CUSTOMER_LIST_PARAMS.SORT_ORDER.DESCENDING,
  };

  const onSearchCustomer = (input: string) => {
    setCustomers([]);
    if (input?.length >= MIN_LENGTH_TO_SEARCH_BY_PHONE) {
      setIsFetching(true);
      orderApis
        .getCustomerListOrEmpty({ ...defaultParams, keyword: input })
        .then((customers) => {
          if (!!customers.length) {
            setCustomers(customers);
          }
        })
        .finally(() => {
          setIsFetching(false);
        });
    }
  };

  const updateCustomer = async (
    orderId: MarketOrder["id"],
    customer: CustomerGetListItem
  ): Promise<{ success: boolean; newCustomer?: IOrderCustomer }> => {
    const { email, userId, id, telephone, name } = customer;
    try {
      setIsFetching(true);
      const payload: OrderCustomerUpdatePayload = {
        customerInfo: {
          email: email,
          id: userId,
          profileId: id,
          phone: telephone,
          name: name,
        },
      };
      await orderApis.updateOrderCustomer(orderId, payload);
      const newCustomer: IOrderCustomer = {
        email: email || "",
        id: id || "",
        name: name || "",
        phone: telephone || "",
      };
      return { success: true, newCustomer };
    } catch (error) {
      return { success: false, newCustomer: undefined };
    } finally {
      setIsFetching(false);
    }
  };

  const upsertOrderCustomer = async (payload: CustomerCreatePayload): Promise<CustomerGetListItem> => {
    try {
      setIsFetching(true);
      const { profile } = await orderApis.upsertOrderCustomer(payload);
      return profile;
    } finally {
      setIsFetching(false);
    }
  };

  return {
    customers,
    isFetching,
    onSearchCustomer,
    updateCustomer,
    upsertOrderCustomer,
  };
};

const useOrderBillingInfo = (orderId: MarketOrder["id"], sellerId: ISeller["id"]) => {
  const [dcaAccounts, setDcaAccounts] = useState<IDcaAccount[]>([]);
  const [isFetchingAccounts, setIsFetchingAccounts] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const searchAccountsByTaxCode = async (taxCode: DcaAccountGetListPayload["taxCode"]) => {
    try {
      setIsFetchingAccounts(true);
      setDcaAccounts([]);
      const { accounts } = await dcaApis.getAccounts({ taxCode, roleId: DcaAccountRoleId.customer, sellerId });
      setDcaAccounts(accounts || []);
      // Update, need to check for all accounts taxCode if there is any match
      const isResultFound = accounts?.some((account) => account.taxCode === taxCode);
      return { success: true, isResultFound };
    } catch (error) {
      return { success: false, isResultFound: false };
    } finally {
      setIsFetchingAccounts(false);
    }
  };

  const updateBillingInfo = async (payload: OrderBillingInfoUpdatePayload) => {
    try {
      setIsUpdating(true);
      await orderApis.updateBillingInfo(orderId, {
        ...payload,
        billingTaxType: payload.billingTaxType || BillingTaxType.VAT,
      });
      return true;
    } catch (error) {
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    dcaAccounts,
    setDcaAccounts,
    isFetchingAccounts,
    isUpdating,
    searchAccountsByTaxCode,
    updateBillingInfo,
  };
};

const useAuthorizedPlatform = (framePlatformId?: number, userAssignedPlatformId?: unknown) => {
  const [isFetching, setIsFetching] = useState(false);
  const [platform, setPlatform] = useState<IPlatform | undefined>(undefined);

  const getPlatformDetail = async (platformId: number) => {
    setIsFetching(true);

    const { data: resp, error } = await tryCatch(
      sellerAndPlatformApis.getListPlatforms({
        platformId,
        page: 1,
        pageSize: 1,
      })
    );

    if (error) {
      setIsFetching(false);
      return;
    }

    if (resp && !!resp.platforms.length) {
      setPlatform(resp.platforms[0]);
    }

    setIsFetching(false);
  };

  useEffect(() => {
    // Priority: framePlatformId > userAssignedPlatformId
    if (!framePlatformId && !userAssignedPlatformId) {
      setPlatform(undefined);
      return;
    }
    if (framePlatformId) {
      getPlatformDetail(framePlatformId);
      return;
    }
    if (userAssignedPlatformId && Number.isInteger(userAssignedPlatformId)) {
      getPlatformDetail(Number(userAssignedPlatformId));
    }
  }, [framePlatformId, userAssignedPlatformId]);

  return {
    isFetching,
    platform,
  };
};

// A shipment rarely has more than a few delivery requests (one per re-creation after a cancellation)
const SHIPMENT_DELIVERY_REQUESTS_PAGE_SIZE = 100;

/** Number of packages of the transport (LOP) carrying the latest delivery request of the shipment, if any. */
const getTransportNumberOfPackages = async (shipmentId: Shipment["shipmentId"]): Promise<number | undefined> => {
  const { data: deliveryRequestsResp } = await tryCatch(
    deliveryApis.getLogisticsDeliveryRequests({
      page: 1,
      pageSize: SHIPMENT_DELIVERY_REQUESTS_PAGE_SIZE,
      documentIds: [shipmentId],
    })
  );
  const latestDeliveryRequest = deliveryRequestUtils.getLatestDeliveryRequestOfShipment(
    deliveryRequestsResp?.deliveryRequests || [],
    shipmentId
  );
  const transportId = latestDeliveryRequest?.currentTransportId;
  if (!transportId) return undefined;

  const { data: transportsResp } = await tryCatch(
    deliveryApis.getLogisticsTransports({ page: 1, pageSize: 1, transportIds: [transportId] })
  );
  const transport = transportsResp?.transports?.find((item) => item.id === transportId);
  return transport?.packageInfo?.numberOfPackages || undefined;
};

/**
 * Number of packages of the shipment's transport. `undefined` when disabled, not found or failed, then the
 * delivery request's number of packages is used instead.
 */
const useTransportNumberOfPackages = (shipmentId: Shipment["shipmentId"], isEnabled: boolean) => {
  const [fetchedResult, setFetchedResult] = useState<{
    shipmentId: Shipment["shipmentId"];
    numberOfPackages?: number;
  }>();

  const shouldFetch = isEnabled && Boolean(shipmentId);

  useEffect(() => {
    // Forget the previous result so that the next lookup never shows a stale number of packages
    if (!shouldFetch) {
      setFetchedResult(undefined);
      return;
    }

    let isCancelled = false;
    const fetchNumberOfPackages = async () => {
      const numberOfPackages = await getTransportNumberOfPackages(shipmentId);
      if (isCancelled) return;
      setFetchedResult({ shipmentId, numberOfPackages });
    };
    fetchNumberOfPackages();

    return () => {
      isCancelled = true;
    };
  }, [shipmentId, shouldFetch]);

  const isFetched = fetchedResult?.shipmentId === shipmentId;

  return {
    numberOfPackages: shouldFetch && isFetched ? fetchedResult?.numberOfPackages : undefined,
    isFetching: shouldFetch && !isFetched,
  };
};

export default {
  useMarketOrderListData,
  useSellerOrderListData,
  useMarketOrderDetail,
  useOrderDetailsForPlatform,
  useOrderDetailsForSeller,
  useSellerOrderDetail,
  useAuthorizedPlatforms,
  useOrderComment,
  useOrderConsultant,
  useOrderCustomer,
  useOrderBillingInfo,
  useAuthorizedPlatform,
  useTransportNumberOfPackages,
};
